/**
 * @documental
 * @export Button
 * @import
 * import { Button } from "capitalrx-components-2.0/lib/general";
 */
import {
  AriaAttributes,
  Children,
  ForwardedRef,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  RefCallback,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { BaseProps } from "../../BaseProps";
import { RefComponent } from "../../forms";
import * as Color from "../Color";
import { useDisabled } from "../DisableGroup/useDisabled";
import { Icon, IconName, IconSize } from "../Icon";
import { Tooltip } from "../Tooltip";
import { Shelf } from "../../layout";
import { createComponentStyleHook } from "../../tokens";
import { Bold, Regular, Semibold } from "../../typography";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { withSubComponents } from "../../utilities/withSubComponents";

import { ButtonAppearance } from "./ButtonAppearances";
import { ButtonClickEventHandler } from "./ButtonClickEventHandler";
import { ButtonSize } from "./ButtonSize";
import { KeyPressEventHandler } from "./KeyPressEventHandler";
import { Placeholder } from "./Placeholder";
import { getAppearanceForString, getCustomAppearanceStyle } from "./helpers";

import tokenStyles from "./styles.module.css";

/**
 * The `Button` component properties.
 */
export interface ButtonProps extends BaseProps {
  /**
   * The button appearance. If none specified defaults to `"primary"`.
   */
  readonly appearance?: "primary" | "secondary" | "text" | "destructive" | "positive" | ButtonAppearance;

  /**
   * Indicates whether the button background should be transparent.
   * @deprecated This should be done through the use of a specific appearance.
   */
  readonly transparent?: boolean;

  /**
   * The button size. If none specified defaults to `"large"`.
   * @deprecated This should be done through the use of a specific appearance.
   */
  readonly size?: ButtonSize;

  /**
   * The button icon. See the [Icon](../Icon/README.md) component for a
   * list of available icons and their names.
   */
  readonly icon?: IconName;

  /**
   * The icon position. If not specified defaults to `"left"`.
   */
  readonly iconPosition?: "left" | "right";

  /**
   * The icon size. If not specified defaults to `"regular"`.
   */
  readonly iconSize?: IconSize;

  /**
   * Indicates whether the button is disabled. If not specified defaults to `false`.
   */
  readonly disabled?: boolean;

  /**
   * Function to call when the button is clicked.
   */
  readonly onClick?: ButtonClickEventHandler;

  /**
   * Function to call when the keyboard is pressed.
   */
  readonly onKeyPress?: KeyPressEventHandler;

  /**
   * The button tab index.
   */
  readonly tabIndex?: number;

  /**
   * The button tooltip.
   */
  readonly tooltip?: ReactNode;

  /**
   * The button children.
   */
  readonly children?: ReactNode;

  /**
   * Automatically focuses the button. If not specified defaults to `false`.
   */
  readonly autoFocus?: boolean;

  /**
   * The ARIA label for the button, used for accessibility.
   */
  readonly "aria-label"?: string;
  /**
   * The ARIA busy for the button, indicates whether the button is currently busy.
   */
  readonly "aria-busy"?: boolean;

  /**
   * The id or space-separated list of element ids that describe the current element.
   */
  readonly "aria-describedby"?: AriaAttributes["aria-describedby"];

  /** Indicates the trigger opens a menu. */
  readonly "aria-haspopup"?: AriaAttributes["aria-haspopup"];

  /** Expanded state for composite widgets like menus. */
  readonly "aria-expanded"?: AriaAttributes["aria-expanded"];

  /** Id of the element controlled by this button. */
  readonly "aria-controls"?: AriaAttributes["aria-controls"];
}

/**
 * The `Button` component sub components.
 */
export interface ButtonSubComponents {
  /**
   * A placeholder component for the Button which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

export const CloseButtonAppearance: ButtonAppearance = {
  padding: { horizontal: 0, vertical: 0 },
  label: {
    letterSpacing: 0,
    uppercase: false,
    weight: "regular",
    fontSize: 16,
    lineHeight: 16,
  },
  icon: {
    size: "regular",
  },
  normal: {
    backgroundColor: Color.Transparent,
    foregroundColor: Color.DarkGrey[500],
    borderColor: Color.Transparent,
  },
  hover: {
    backgroundColor: Color.Transparent,
    foregroundColor: Color.DarkGrey[400],
    borderColor: Color.Transparent,
  },
  pressed: {
    backgroundColor: Color.Transparent,
    foregroundColor: Color.DarkGrey[600],
    borderColor: Color.Transparent,
  },
  disabled: {
    backgroundColor: Color.Transparent,
    foregroundColor: Color.DarkGrey[400],
    borderColor: Color.Transparent,
  },
};

/**
 * A regular button.
 */

const useResolvedButtonStyles = createComponentStyleHook<ButtonProps>({
  legacyStyles: () => import("./legacy/styles.module.scss"),
  tokenStyles,
  componentTokens: "button",
});

export const Button = withSubComponents<RefComponent<HTMLButtonElement, ButtonProps>, ButtonSubComponents>(
  {
    Placeholder,
  },
  forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, outerRef) {
    const {
      appearance = "primary",
      size = "large",
      icon,
      iconPosition = "left",
      iconSize = "regular",
      children,
      tooltip,
      transparent = false,
      tabIndex = 0,
      autoFocus = false,
      onClick,
      onKeyPress,
      "aria-label": ariaLabel,
      "aria-busy": ariaBusy,
      "aria-describedby": ariaDescribedby,
      "aria-haspopup": ariaHaspopup,
      "aria-expanded": ariaExpanded,
      "aria-controls": ariaControls,
    } = props;

    const { mode, StyleModeBoundary } = useResolvedButtonStyles(props);
    const disabled = useDisabled(props);

    const isStringAppearance = typeof appearance === "string";
    const appearanceData = isStringAppearance ? getAppearanceForString(appearance, transparent, size) : appearance;
    const resolvedSize = isStringAppearance ? size : (appearanceData.className?.match(/\b(large|small|compact)\b/)?.[1] ?? size);

    const nonEmptyChildren = Children.toArray(children).filter((child) => {
      if (child === null || child === undefined || child === "") return false;
      return true;
    });

    const iconOnly = !!(icon && nonEmptyChildren.length === 0);

    const hasWarnedRef = useRef(false);
    useEffect(() => {
      if (process.env.NODE_ENV !== "production" && iconOnly && !ariaLabel && !hasWarnedRef.current) {
        hasWarnedRef.current = true;
        console.warn(
          `Button: Icon-only buttons should have an 'aria-label' for accessibility.\n`
          + `Button with icon "${icon}" is missing an aria-label.\n`
          + `Screen reader users won't know what this button does.\n`
          + `Example: <Button icon="${icon}" aria-label="Descriptive action text" />`,
        );
      }
    }, [iconOnly, ariaLabel, icon]);

    const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
      className: [
        tokenStyles.Button,
        "button",
        isStringAppearance ? appearance : "custom",
        resolvedSize,
        {
          "icon-only": iconOnly,
          transparent,
          disabled,
        },
      ],
      testIdPrefix: "button",
    });

    const clickHandler = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        if (disabled) return;
        onClick?.(e);
      },
      [disabled, onClick],
    );

    const keyPressHandler = useCallback(
      (e: KeyboardEvent<HTMLButtonElement>) => {
        if (disabled) return;
        onKeyPress?.(e);
      },
      [disabled, onKeyPress],
    );

    const previousOuterRef = useRef<ForwardedRef<HTMLButtonElement> | null>(null);
    const previousInnerRef = useRef<RefCallback<HTMLButtonElement> | null>(null);
    const previousRefHandler = useRef<RefCallback<HTMLButtonElement> | null>(null);

    const refHandler = useCallback(
      (innerRef?: RefCallback<HTMLButtonElement>) => {
        if (previousInnerRef.current === innerRef && previousOuterRef.current === outerRef && previousRefHandler.current !== null) {
          return previousRefHandler.current;
        }

        previousInnerRef.current = innerRef ?? null;
        previousOuterRef.current = outerRef ?? null;

        return (previousRefHandler.current = (ref: HTMLButtonElement | null) => {
          if (innerRef) innerRef(ref);
          if (outerRef) {
            if (typeof outerRef === "function") outerRef(ref);
            else outerRef.current = ref;
          }
        });
      },
      [outerRef],
    );

    let LabelContainer = Regular;
    if (appearanceData.label.weight === "bold") LabelContainer = Bold;
    else if (appearanceData.label.weight === "semibold") LabelContainer = Semibold;

    const iconElement = (position: "left" | "right") =>
      icon && (position === iconPosition || (position === "left" && iconOnly)) ? (
        <span className={tokenStyles.Icon}>
          <Icon
            name={icon}
            size={iconSize ?? appearanceData?.icon?.size ?? "regular"}
            aria-label={ariaLabel ? `${ariaLabel} icon` : undefined}
          />
        </span>
      ) : null;

    const buttonContent = (
      <Shelf spacing="small">
        {iconElement("left")}
        {!iconOnly && <LabelContainer>{children ?? <>&nbsp;</>}</LabelContainer>}
        {!iconOnly && iconElement("right")}
      </Shelf>
    );

    const sharedButtonProps = {
      tabIndex,
      disabled,
      autoFocus,
      type: "button" as const,
      onClick: clickHandler,
      onKeyDown: keyPressHandler,
      "aria-label": ariaLabel,
      "aria-busy": ariaBusy,
      "aria-describedby": ariaDescribedby,
      "aria-haspopup": ariaHaspopup,
      "aria-expanded": ariaExpanded,
      "aria-controls": ariaControls,
    };

    const customStyle = useMemo(
      () => (!isStringAppearance ? getCustomAppearanceStyle(appearanceData, iconOnly) : undefined),
      [isStringAppearance, appearanceData, iconOnly],
    );

    const content = (ref?: RefCallback<HTMLButtonElement>) => (
      <button {...baseHtmlProps} {...sharedButtonProps} style={{ ...baseHtmlProps.style, ...customStyle }} ref={refHandler(ref)}>
        {buttonContent}
      </button>
    );

    return (
      <StyleModeBoundary mode={mode}>
        {tooltip && <Tooltip content={tooltip}>{content}</Tooltip>}
        {!tooltip && content()}
      </StyleModeBoundary>
    );
  }),
);
